/* Collectors Guild: the star pop. A click on a button or a product card sends a handful of tiny print stars, yellow
   and blue, out of the click point; they are gone in just over half a second. A keyboard press bursts from the
   control's centre. Plain text links stay quiet, disabled controls too, and nothing pops under reduced motion.
   The stars live in one fixed, aria-hidden layer that never takes the pointer, and each removes itself.
   Taste numbers: --cg-pop-count, --cg-pop-distance, --cg-pop-size and --cg-pop-duration on :root
   (guild-editorial.css). The press itself (the sink and spring back) is CSS. */
(() => {
  if (window.CollectorsGuildPress) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const BUTTONS = 'button, [role="button"], summary, .button, .cg-button, .cg-e-pixel-button, .cg-e-boxed-link, .cg-e-primary, .cg-product-view, .cg-trust-index a';
  const CARDS = '.cg-product-card, .cg-e-strip-card, .cg-e-format, .cg-e-guide-item';
  const QUIET = '[data-cg-pop="off"], .cg-e-rig, input, select, textarea, label, [name="checkout"], .shopify-payment-button, .additional-checkout-buttons, .quantity, .quantity__button';
  let layer = null;
  const number = (style, name, fallback) => parseFloat(style.getPropertyValue(name)) || fallback;

  function burst(x, y, options = {}) {
    if (reduce.matches || document.hidden) return;
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'cg-pop-layer';
      layer.setAttribute('aria-hidden', 'true');
      document.body.append(layer);
    }
    const style = getComputedStyle(document.documentElement);
    const count = Math.round(options.count ?? number(style, '--cg-pop-count', 6));
    const reach = options.reach ?? number(style, '--cg-pop-distance', 48);
    const size = options.size ?? number(style, '--cg-pop-size', 24);
    const duration = options.duration ?? number(style, '--cg-pop-duration', 560), turn = Math.random() * Math.PI * 2;
    for (let i = 0; i < count; i++) {
      const star = document.createElement('span');
      star.className = `cg-pop-star${i % 2 ? ' cg-pop-star--blue' : ''}`;
      star.style.left = `${x}px`;
      star.style.top = `${y}px`;
      // Each burst keeps its own size, even when another card reveals at the same time.
      star.style.width = `${size}px`;
      layer.append(star);
      const angle = turn + (i / count) * Math.PI * 2 + (Math.random() - .5) * .5;
      const d = reach * (.75 + Math.random() * .5), dx = Math.cos(angle) * d, dy = Math.sin(angle) * d;
      const spin = (Math.random() < .5 ? -1 : 1) * (90 + Math.random() * 90);
      // Out fast to full size over the first half, then drift a little further while shrinking and fading.
      const motion = star.animate([
        { transform: 'translate(-50%,-50%) scale(.2) rotate(0deg)', opacity: 1, easing: 'cubic-bezier(.23,1,.32,1)' },
        { transform: `translate(calc(-50% + ${dx.toFixed(1)}px),calc(-50% + ${dy.toFixed(1)}px)) scale(1) rotate(${(spin * .6).toFixed(0)}deg)`, opacity: 1, offset: .5, easing: 'cubic-bezier(.55,0,1,.45)' },
        { transform: `translate(calc(-50% + ${(dx * 1.18).toFixed(1)}px),calc(-50% + ${(dy * 1.18).toFixed(1)}px)) scale(.3) rotate(${spin.toFixed(0)}deg)`, opacity: 0 },
      ], { duration, easing: 'linear' });
      motion.onfinish = motion.oncancel = () => star.remove();
    }
  }

  const controlFor = event => {
    if (reduce.matches || !event.isTrusted || !(event.target instanceof Element)) return null;
    const control = event.target.closest(`${BUTTONS}, ${CARDS}`);
    if (!control || event.target.closest(QUIET) || control.matches(':disabled, [aria-disabled="true"]')) return null;
    return control;
  };
  // A press bursts on release (pointerup), at window capture so nothing upstream can swallow it first (Shopify's theme
  // editor handles clicks in its preview for its block inspector). A press that moves (a scroll) or is cancelled pops
  // nothing. The click that follows is then skipped; a click with no press before it (a key press) still bursts.
  let pressed = null, popped = null;
  window.addEventListener('pointerdown', event => {
    const control = controlFor(event);
    pressed = control && event.button === 0 ? { id: event.pointerId, x: event.clientX, y: event.clientY, at: performance.now(), control } : null;
  }, true);
  window.addEventListener('pointercancel', () => { pressed = null; }, true);
  window.addEventListener('pointerup', event => {
    const press = pressed;
    pressed = null;
    if (!press || event.pointerId !== press.id || performance.now() - press.at > 900) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) return;
    if (!press.control.contains(document.elementFromPoint(event.clientX, event.clientY))) return;
    burst(event.clientX, event.clientY);
    popped = { control: press.control, at: performance.now() };
  }, true);
  window.addEventListener('click', event => {
    const control = controlFor(event);
    if (!control) return;
    if (popped && popped.control === control && performance.now() - popped.at < 700) { popped = null; return; }
    let x = event.clientX, y = event.clientY;
    if (event.detail === 0 || (!x && !y)) {
      const r = control.getBoundingClientRect();
      if (!r.width) return;
      x = r.left + r.width / 2; y = r.top + r.height / 2;
    }
    burst(x, y);
  }, true);
  // iOS Safari applies :active (the CSS press) only once the page listens for touches.
  document.addEventListener('touchstart', () => {}, { passive: true });

  reduce.addEventListener('change', () => {
    if (!reduce.matches) return;
    pressed = popped = null;
    layer?.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
  });

  window.CollectorsGuildPress = { burst };
})();
